import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useWorkspaces } from "@/hooks/useWorkspaces";

interface Props {
  variant: "desktop" | "mobile";
  onNavigate?: () => void;
}

export default function WorkspaceSwitcher({ variant, onNavigate }: Props) {
  const { t } = useTranslation();
  const workspaces = useWorkspaces();
  const portals = workspaces.filter((w) => w.key !== "account");
  const primary = portals[0] ?? workspaces[0];
  const label = (w: typeof primary) => t(w.labelKey, w.fallback);

  const btnClass = variant === "desktop"
    ? "font-semibold h-10 px-4 border-white/20 bg-white/[0.06] text-white hover:bg-white/10 text-sm"
    : "w-full font-semibold h-12 text-base border-white/20 bg-white/[0.06] text-white hover:bg-white/10 active:scale-[0.98] transition-transform";

  // Only one portal (or plain customer): keep the single button.
  if (portals.length <= 1) {
    return (
      <Button variant="outline" size={variant === "desktop" ? "sm" : "default"} className={btnClass} asChild>
        <Link to={primary.href} onClick={onNavigate}>{label(primary)}</Link>
      </Button>
    );
  }

  if (variant === "mobile") {
    return (
      <div className="flex flex-col gap-2">
        <div className="px-1 text-xs font-bold uppercase tracking-wider text-white/50">
          {t("nav.switchWorkspace", "Switch workspace")}
        </div>
        {workspaces.map((w) => (
          <Button key={w.key} variant="outline" className={btnClass} asChild>
            <Link to={w.href} onClick={onNavigate}>{label(w)}</Link>
          </Button>
        ))}
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className={cn(btnClass, "gap-1.5")}>
          {t("nav.myWorkspaces", "My workspaces")}
          <ChevronDown className="h-4 w-4" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[200px] rounded-none">
        {workspaces.map((w) => (
          <DropdownMenuItem key={w.key} asChild className="rounded-none cursor-pointer">
            <Link to={w.href}>{label(w)}</Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
